import type { Viewer } from '@xeokit/xeokit-sdk';

interface PointerLensConfig {
  active?: boolean;
  containerId?: string;
  lensPosToggle?: boolean;
  lensPosToggleAmount?: number;
  lensPosMarginLeft?: number;
  lensPosMarginTop?: number;
  zoomLevel?: number;
  lensParams?: LensParams;
}

interface LensParams {
  canvasSize: number;
  cursorBorder: number;
  cursorSize: number;
}

export class PointerLens {
  private viewer: Viewer;
  private _lensCursorDiv: HTMLDivElement;
  private _lensParams: LensParams;
  private _lensContainer: HTMLDivElement;
  private _lensContainerId: string;
  private _lensCanvas: HTMLCanvasElement;
  private _lensCanvasContext: CanvasRenderingContext2D | null;
  private _canvasElement: HTMLCanvasElement;
  private _canvasPos: [number, number] | null = null;
  private _snappedCanvasPos: [number, number] | null = null;
  private _lensPosToggle: boolean;
  private _lensPosToggleAmount: number;
  private _lensPosMarginLeft: number;
  private _lensPosMarginTop: number;
  private _zoomLevel: number;
  private _active: boolean;
  private _visible: boolean;
  private _snapped: boolean = false;
  private _destroyed: boolean = false;
  private _onViewerRendering: string;

  private _abortController: AbortController | null = null;

  /**
   * Constructs a new PointerLens.
   * @param viewer The Viewer
   * @param cfg PointerLens configuration.
   * @param cfg.active Whether PointerLens is active. The PointerLens can only be shown when this is `true` (default: true).
   */
  constructor(viewer: any, cfg: PointerLensConfig = {}) {
    this.viewer = viewer;

    this._lensCursorDiv = document.createElement('div');
    this._lensParams = {
      canvasSize: cfg.lensParams?.canvasSize || 300,
      cursorBorder: cfg.lensParams?.cursorBorder || 2,
      cursorSize: cfg.lensParams?.cursorSize || 10,
    };
    this._lensCursorDiv.style.borderRadius = '50%';
    this._lensCursorDiv.style.width = this._lensParams.cursorSize + 'px';
    this._lensCursorDiv.style.height = this._lensParams.cursorSize + 'px';
    this._lensCursorDiv.style.zIndex = '100000';
    this._lensCursorDiv.style.position = 'absolute';
    this._lensCursorDiv.style.pointerEvents = 'none';

    this._lensContainer = document.createElement('div');
    this._lensContainerId = cfg.containerId || 'xeokit-lens';
    this._lensContainer.setAttribute('id', this._lensContainerId);

    this._lensContainer.style.border = '1px solid black';
    this._lensContainer.style.background = 'white';
    //    this._lensContainer.style.opacity = "0";
    this._lensContainer.style.borderRadius = '50%';
    this._lensContainer.style.width = this._lensParams.canvasSize + 'px';
    this._lensContainer.style.height = this._lensParams.canvasSize + 'px';

    this._lensContainer.style.zIndex = '15000';
    this._lensContainer.style.position = 'absolute';
    this._lensContainer.style.pointerEvents = 'none';
    this._lensContainer.style.visibility = 'hidden';

    this._lensCanvas = document.createElement('canvas');
    this._lensCanvas.id = `${this._lensContainerId}-canvas`;
    // this._lensCanvas.style.background = "darkblue";
    this._lensCanvas.style.borderRadius = '50%';

    this._lensCanvas.style.width = this._lensParams.canvasSize + 'px';
    this._lensCanvas.style.height = this._lensParams.canvasSize + 'px';
    this._lensCanvas.style.zIndex = '15000';
    this._lensCanvas.style.pointerEvents = 'none';

    document.body.appendChild(this._lensContainer);
    this._lensContainer.appendChild(this._lensCanvas);
    this._lensContainer.appendChild(this._lensCursorDiv);

    this._lensCanvasContext = this._lensCanvas.getContext('2d');
    // @ts-ignore xeokit
    this._canvasElement = this.viewer.scene.canvas.canvas;

    this._canvasPos = null;
    this._snappedCanvasPos = null;
    this._lensPosToggle = cfg.lensPosToggle || true;
    this._lensPosToggleAmount = cfg.lensPosToggleAmount || 300;
    this._lensPosMarginLeft = cfg.lensPosMarginLeft || 25;
    this._lensPosMarginTop = cfg.lensPosMarginTop || 25;
    this._lensContainer.style.marginTop = `${this._lensPosMarginTop}px`;
    this._lensContainer.style.marginLeft = `${this._lensPosMarginLeft}px`;

    this._zoomLevel = cfg.zoomLevel || 2;

    this._active = cfg.active !== false;
    this._visible = false;
    this.snapped = false;

    this._onViewerRendering = this.viewer.scene.on('rendering', () => {
      if (this._active && this._visible) {
        this.update();
      }
    });

    this._abortController = new AbortController();

    // On window resize, change width and height of pointer lens
    window.addEventListener(
      'resize',
      () => {
        this.resizePointerLens();
      },
      { signal: this._abortController.signal },
    );

    this.resizePointerLens();
  }

  resizePointerLens() {
    // Make size relative to window height, but not smaller than 150px and not bigger than 300px
    const size = Math.min(Math.max(window.innerHeight / 3, 150), 300);

    this._lensParams = { ...this._lensParams, ...{ canvasSize: size } };

    this._lensCursorDiv.style.width = this._lensParams.cursorSize + 'px';
    this._lensCursorDiv.style.height = this._lensParams.cursorSize + 'px';
    this._lensContainer.style.width = this._lensParams.canvasSize + 'px';
    this._lensContainer.style.height = this._lensParams.canvasSize + 'px';
    this._lensCanvas.style.width = this._lensParams.canvasSize + 'px';
    this._lensCanvas.style.height = this._lensParams.canvasSize + 'px';

    this._lensPosToggleAmount = size / 2;
  }

  /**
   * Updates this PointerLens.
   */
  update() {
    if (!this._active || !this._visible) {
      return;
    }
    if (!this._canvasPos) {
      return;
    }
    const lensRect = this._lensContainer.getBoundingClientRect();
    const canvasRect = this._canvasElement.getBoundingClientRect();
    const pointerOnLens =
      this._canvasPos[0] < lensRect.right &&
      this._canvasPos[0] > lensRect.left &&
      this._canvasPos[1] < lensRect.bottom &&
      this._canvasPos[1] > lensRect.top;
    this._lensContainer.style.marginLeft = `${this._lensPosMarginLeft}px`;
    if (pointerOnLens) {
      if (this._lensPosToggle) {
        this._lensContainer.style.marginTop = `${canvasRect.bottom - canvasRect.top - this._lensCanvas.height - this._lensPosToggleAmount}px`;
      } else {
        this._lensContainer.style.marginTop = `${this._lensPosMarginTop}px`;
      }
      this._lensPosToggle = !this._lensPosToggle;
    }
    this._lensCanvasContext?.clearRect(0, 0, this._lensCanvas.width, this._lensCanvas.height);
    const size = Math.max(this._lensCanvas.width, this._lensCanvas.height) / this._zoomLevel;
    this._lensCanvasContext?.drawImage(
      this._canvasElement, // source canvas
      this._canvasPos[0] - size / 2, // source x (zoom center)
      this._canvasPos[1] - size / 2, // source y (zoom center)
      size, // source width
      size, // source height
      0, // destination x
      0, // destination y
      this._lensCanvas.width, // destination width
      this._lensCanvas.height, // destination height
    );

    const middle =
      this._lensParams.canvasSize / 2 -
      this._lensParams.cursorSize / 2 -
      this._lensParams.cursorBorder;

    const deltaX = this._snappedCanvasPos ? this._snappedCanvasPos[0] - this._canvasPos[0] : 0;
    const deltaY = this._snappedCanvasPos ? this._snappedCanvasPos[1] - this._canvasPos[1] : 0;

    this._lensCursorDiv.style.left = `${middle + deltaX * this._zoomLevel}px`;
    this._lensCursorDiv.style.top = `${middle + deltaY * this._zoomLevel}px`;
  }

  set zoomLevel(zoomLevel: number) {
    this._zoomLevel = zoomLevel;
    this.update();
  }

  get zoomLevel(): number {
    return this._zoomLevel;
  }

  set canvasPos(canvasPos: [number, number]) {
    this._canvasPos = canvasPos;
    this.update();
  }

  get canvasPos(): [number, number] | null {
    return this._canvasPos;
  }

  set snappedCanvasPos(snappedCanvasPos: [number, number]) {
    this._snappedCanvasPos = snappedCanvasPos;
    this.update();
  }

  get snappedCanvasPos(): [number, number] | null {
    return this._snappedCanvasPos;
  }

  private set snapped(snapped: boolean) {
    this._snapped = snapped;
    const [bg, border] = snapped ? ['greenyellow', 'green'] : ['pink', 'red'];
    this._lensCursorDiv.style.background = bg;
    this._lensCursorDiv.style.border = this._lensParams.cursorBorder + 'px solid ' + border;
  }

  private get snapped(): boolean {
    return this._snapped;
  }

  _updateActiveVisible() {
    this._lensContainer.style.visibility = this._active && this._visible ? 'visible' : 'hidden';
    this.update();
  }

  set active(active: boolean) {
    this._active = active;
    this._updateActiveVisible();
  }

  get active(): boolean {
    return this._active;
  }

  private set visible(visible: boolean) {
    this._visible = visible;
    this._updateActiveVisible();
  }

  private get visible(): boolean {
    return this._visible;
  }

  destroy() {
    if (!this._destroyed) {
      this.viewer.scene.off(this._onViewerRendering);
      this._lensContainer.removeChild(this._lensCanvas);
      document.body.removeChild(this._lensContainer);
      this._destroyed = true;
      this._abortController?.abort();
    }
  }
}
